import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import distributionApi from 'services/api/distribution';

type UploadAudio = ReturnType<typeof distributionApi.useUploadAudioMutation>;
type UploadAudioRequest = ReturnType<UploadAudio[0]>;

export type AudioFileType = {
  audioFile: File | null;
  setAudioFile: (audioFile: File | null) => void;
  validationError: Error | null;
  setValidationError: (error: Error | null) => void;
  setUploadRequest: (request: UploadAudioRequest) => void;
  cancelUploadRequest: () => void;
};

export const AudioFileContext = createContext<AudioFileType>(
  {} as AudioFileType
);

export const useAudioFileContext = () => useContext(AudioFileContext);

export const AudioFileProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<Error | null>(null);

  const uploadRequest = useRef<UploadAudioRequest | null>(null);

  useEffect(() => {
    if (audioFile) {
      return;
    }
    setValidationError(null);
  }, [audioFile]);

  const setUploadRequest = useCallback((request: UploadAudioRequest) => {
    uploadRequest.current = request;
  }, []);

  const cancelUploadRequest = useCallback(() => {
    const currentRequest = uploadRequest.current;

    if (!currentRequest) {
      return;
    }

    currentRequest.abort();
    currentRequest.reset();
    uploadRequest.current = null;
  }, []);

  return (
    <AudioFileContext.Provider
      value={{
        audioFile,
        setAudioFile,
        validationError,
        setValidationError,
        setUploadRequest,
        cancelUploadRequest,
      }}
    >
      {children}
    </AudioFileContext.Provider>
  );
};
